import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Upload, Plus, Trash2, ShieldCheck, Save, Eraser } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, inr } from "@/lib/api";
import { PageHeader, th } from "@/components/common";

const COLS = [["booking_date", "Booking Date", 110], ["lr_number", "LR / Builty No *", 110], ["party", "Party Name *", 170], ["consignee", "Consignee", 150],
  ["vehicle_number", "Vehicle No", 120], ["from_location", "From *", 110], ["to_location", "To *", 110], ["material", "Material", 120], ["quantity", "Qty", 60],
  ["weight", "Weight", 70], ["weight_unit", "Unit (MT/KG)", 80], ["packages", "Pkgs", 60], ["freight_type", "Freight Type", 100], ["rate", "Rate / Fixed ₹", 100],
  ["advance", "Advance ₹", 90], ["vendor", "Vendor Name", 150], ["hire_type", "Hire Type", 90], ["hire_rate", "Hire Rate / Fixed ₹", 110], ["driver_name", "Driver Name", 120], ["driver_mobile", "Driver Mobile", 110], ["status", "Status", 100], ["remarks", "Remarks", 140]];
const blank = () => Object.fromEntries(COLS.map(([k]) => [k, k === "weight_unit" ? "MT" : k === "freight_type" ? "Fixed" : ""]));
const LISTS = { freight_type: ["Fixed", "Per MT"], hire_type: ["Fixed", "Per MT"], weight_unit: ["MT", "KG"], status: ["Created", "Loading", "In-Transit", "Delivered"] };

function Cell({ r, c, value, err, onChange, onPaste, list, sticky }) {
  return (
    <td className={`border p-0 ${sticky ? "sticky left-[42px] z-10 bg-card" : ""} ${err ? "bg-red-50 dark:bg-red-950/40" : ""}`} title={err || ""}>
      <input data-testid={`bulk-cell-${r}-${c}`} value={value} list={list} onChange={(e) => onChange(e.target.value)} onPaste={onPaste}
        className={`h-8 w-full bg-transparent px-2 text-xs outline-none focus:ring-2 focus:ring-primary ${err ? "text-red-700 ring-1 ring-inset ring-red-500" : ""}`} />
    </td>
  );
}

function Lists({ parties, vehicles, vendors }) {
  return (
    <>
      <datalist id="dl-party">{parties.map((p) => <option key={p.id} value={p.name} />)}</datalist>
      <datalist id="dl-vendor">{vendors.map((v) => <option key={v.id} value={v.name} />)}</datalist>
      <datalist id="dl-vehicle">{vehicles.map((v) => <option key={v.id} value={v.vehicle_number} />)}</datalist>
      {Object.entries(LISTS).map(([k, xs]) => <datalist key={k} id={`dl-${k}`}>{xs.map((x) => <option key={x} value={x} />)}</datalist>)}
    </>
  );
}

const listFor = (k) => (k === "party" ? "dl-party" : k === "vendor" ? "dl-vendor" : k === "vehicle_number" ? "dl-vehicle" : LISTS[k] ? `dl-${k}` : undefined);

function Grid({ rows, setRows, check }) {
  const set = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const paste = (i, ci) => (e) => {
    const t = e.clipboardData.getData("text");
    if (!/[\t\n]/.test(t)) return;
    e.preventDefault();
    const lines = t.replace(/\r/g, "").split("\n").filter((l, n, a) => l.length || n < a.length - 1);
    setRows((rs) => {
      const out = [...rs];
      lines.forEach((l, n) => {
        const row = { ...(out[i + n] || blank()) };
        l.split("\t").forEach((v, m) => { const col = COLS[ci + m]; if (col) row[col[0]] = v.trim(); });
        out[i + n] = row;
      });
      return out;
    });
  };
  return (
    <div className="overflow-auto rounded-xl border bg-card" style={{ maxHeight: "60vh" }} data-testid="bulk-grid">
      <table className="border-collapse text-left">
        <thead className="sticky top-0 z-20"><tr><th className={`${th} !bg-secondary sticky left-0 z-30 w-[42px] min-w-[42px]`}>#</th>{COLS.map(([k, l, w], ci) => <th key={k} className={`${th} !bg-secondary ${ci === 1 ? "sticky left-[42px] z-30" : ""}`} style={{ minWidth: w }}>{l}</th>)}<th className={`${th} !bg-secondary`}>Balance</th><th className={`${th} !bg-secondary`}>Hire ₹</th><th className={`${th} !bg-secondary`}></th></tr></thead>
        <tbody>{rows.map((r, i) => {
          const ck = check?.[i];
          return (
            <tr key={i} data-testid={`bulk-row-${i}`} className={ck && Object.keys(ck.errors).length ? "bg-red-50/40 dark:bg-red-950/10" : ""}>
              <td className="sticky left-0 z-10 w-[42px] min-w-[42px] border bg-card px-2 text-xs text-muted-foreground">{i + 1}</td>
              {COLS.map(([k], ci) => <Cell key={k} r={i} c={k} sticky={ci === 1} value={r[k] ?? ""} err={ck?.errors[k]} list={listFor(k)} onChange={(v) => set(i, k, v)} onPaste={paste(i, ci)} />)}
              <td className="border px-2 text-right text-xs font-semibold" data-testid={`bulk-balance-${i}`}>{ck ? inr(ck.balance, 2) : ""}</td>
              <td className="border px-2 text-right text-xs" data-testid={`bulk-hire-${i}`}>{ck?.hire_freight ? inr(ck.hire_freight, 2) : ""}</td>
              <td className="border px-1"><button type="button" data-testid={`bulk-del-${i}`} title="Remove row" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-red-600" /></button></td>
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  );
}

function Errors({ rows, check }) {
  const bad = (check || []).map((c, i) => [i, c]).filter(([, c]) => Object.keys(c.errors).length);
  if (!check) return null;
  if (!bad.length) return <p className="rounded-lg bg-emerald-50 p-3 text-sm font-semibold text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300" data-testid="bulk-all-ok">Sab {rows.length} lines sahi hain — ab "Process & Save All" dabayein.</p>;
  const label = Object.fromEntries(COLS.map(([k, l]) => [k, l.replace(" *", "")]));
  return (
    <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm dark:bg-red-950/30" data-testid="bulk-error-list">
      <p className="mb-1 font-bold text-red-700">{bad.length} line me galti hai (laal cell):</p>
      <ul className="max-h-40 space-y-0.5 overflow-auto text-xs text-red-800 dark:text-red-300">{bad.map(([i, c]) => <li key={i}>Row {i + 1}: {Object.entries(c.errors).map(([k, m]) => `${label[k]} — ${m}`).join("; ")}</li>)}</ul>
    </div>
  );
}

export default function BulkUpload() {
  const [rows, setRows] = useState(() => Array.from({ length: 10 }, blank));
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [parties, setParties] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vendors, setVendors] = useState([]);
  const fileRef = useRef();
  useEffect(() => {
    api.get("/parties").then((r) => setParties(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/market/vendors").then((r) => setVendors(r.data)).catch(() => {});
  }, []);
  const edit = (fn) => { setRows(fn); setCheck(null); };
  const filled = () => rows.filter((r) => COLS.some(([k]) => !["weight_unit", "freight_type"].includes(k) && String(r[k] || "").trim()));
  const template = async () => {
    try {
      const r = await api.get("/bulk-lr/template", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = "bulk_lr_template.xlsx"; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post("/bulk-lr/parse", fd); edit(r.data.rows.length ? r.data.rows : [blank()]); setDone(null); toast.success(`${r.data.rows.length} lines loaded — ab Validate karein`); } catch (e) { toast.error(errMsg(e)); }
    fileRef.current.value = "";
  };
  const validate = async () => {
    const rs = filled();
    if (!rs.length) return toast.error("Kam se kam ek line bharein");
    setBusy(true);
    try { setRows(rs); const r = await api.post("/bulk-lr/validate", { rows: rs }); setCheck(r.data.rows); toast[r.data.error_count ? "error" : "success"](r.data.error_count ? `${r.data.error_count} line me galti` : "Sab sahi hai"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const save = async () => {
    const rs = filled();
    if (!rs.length) return toast.error("Kam se kam ek line bharein");
    setBusy(true);
    try {
      const r = await api.post("/bulk-lr/commit", { rows: rs });
      setDone(r.data); edit(Array.from({ length: 10 }, blank)); toast.success(`${r.data.created} Party Booking + LR ban gaye`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.rows) { setRows(rs); setCheck(d.rows); toast.error(d.message); } else toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <div className="space-y-5" data-testid="bulk-upload-page">
      <PageHeader title="Bulk LR & Booking Upload" subtitle="बल्क बुकिंग और एलआर अपलोड — Excel upload ya neeche grid me type / paste karein">
        <Button variant="outline" data-testid="bulk-template-btn" onClick={template}><Download className="mr-1 h-4 w-4" />Download Template</Button>
        <Button variant="outline" data-testid="bulk-upload-btn" onClick={() => fileRef.current.click()}><Upload className="mr-1 h-4 w-4" />Upload Excel</Button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="bulk-file-input" onChange={(e) => upload(e.target.files[0])} />
      </PageHeader>
      <Lists parties={parties} vehicles={vehicles} vendors={vendors} />
      <p className="text-xs text-muted-foreground">Har line se ek Party Booking aur uska LR banega. Party Name Party Master se match hona chahiye. Fleet me na mili gaadi Market vehicle maani jaayegi — Vendor Name aur Hire bharne par uski Market Trip bhi apne aap banegi. Status khaali = Created. Excel se kai lines copy karke kisi bhi cell me paste kar sakte hain.</p>
      <Grid rows={rows} setRows={edit} check={check} />
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" data-testid="bulk-add-rows" onClick={() => edit((rs) => [...rs, ...Array.from({ length: 5 }, blank)])}><Plus className="mr-1 h-4 w-4" />5 Rows</Button>
        <Button variant="outline" data-testid="bulk-clear" onClick={() => { edit(Array.from({ length: 10 }, blank)); setDone(null); }}><Eraser className="mr-1 h-4 w-4" />Clear</Button>
        <Button variant="outline" data-testid="bulk-validate" disabled={busy} onClick={validate}><ShieldCheck className="mr-1 h-4 w-4" />Validate</Button>
        <Button data-testid="bulk-save-all" disabled={busy} onClick={save}><Save className="mr-1 h-4 w-4" />{busy ? "Processing…" : "Process & Save All Bookings"}</Button>
      </div>
      <Errors rows={rows} check={check} />
      {done && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm dark:bg-emerald-950/30" data-testid="bulk-result">
          <p className="mb-1 font-bold text-emerald-800 dark:text-emerald-300">{done.created} bookings saved</p>
          <div className="flex flex-wrap gap-2 text-xs">{done.items.map((x) => <Link key={x.id} to={`/bookings/${x.id}`} className="rounded bg-white px-2 py-0.5 font-mono text-primary dark:bg-card">{x.booking_no} · {x.lr_number}</Link>)}</div>
        </div>
      )}
    </div>
  );
}
